import { Component, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { Mutation } from 'vuex';
import { Action, ActionStatus } from '@/application/types';
import { showError } from '@/application/snackbar/service';
import MasterDataTab from '../master-data-tab/master-data-tab.vue';
import PositionsTab from '../positions-tab/positions-tab.vue';
import StatusHistoryTab from '../status-history-tab/status-history-tab.vue';
import DataDistributionTab from '../data-distribution-tab/data-distribution-tab.vue';
import { Order } from '../../types';

const orderSearchState = namespace('orderSearch');

const defaultTab = 'tab-master-data';

@Component({
  components: {
    MasterDataTab,
    PositionsTab,
    StatusHistoryTab,
    DataDistributionTab,
  }
})
export default class OrderDetails extends Vue {

  @orderSearchState.Action
  readonly getCurrentOrder!: Action<void>;

  @orderSearchState.Mutation
  readonly setCurrentOrderId!: Mutation<string>;

  @orderSearchState.State
  readonly getOrderIdentificationsByOrderNumberStatus!: ActionStatus;

  @orderSearchState.State
  readonly getCurrentOrderStatus!: ActionStatus;

  @orderSearchState.State
  readonly currentOrderId!: string | null;

  @orderSearchState.State
  readonly currentOrder!: Order | null;

  currentTab = defaultTab;

  get isLoading(): boolean {
    return this.getCurrentOrderStatus === ActionStatus.InProgress
      || this.getOrderIdentificationsByOrderNumberStatus === ActionStatus.InProgress;
  }

  get jobSummary(): string {
    if (this.isLoading) {
      return 'Verteilung: Jobs laden...';
    }

    const lines = [];

    if (this.hasDoneJobs) {
      lines.push(`Verteilung: ${this.numberOfDoneJobs} ${this.numberOfDoneJobs === 1 ? 'Job' : 'Jobs'} abgeschlossen`);
    }
    if (this.hasScheduledJobs) {
      lines.push(`Verteilung: ${this.numberOfScheduledJobs} ${this.numberOfScheduledJobs === 1 ? 'Job' : 'Jobs'} geplant`);
    }
    if (this.hasFailedJobs) {
      lines.push(`Verteilung: ${this.numberOfFailedJobs} ${this.numberOfFailedJobs === 1 ? 'Job' : 'Jobs'} fehlgeschlagen`);
    }

    if (lines.length === 0) {
      return 'Verteilung: Keine Jobs vorhanden';
    }

    return lines.join(', ');
  }

  get hasDoneJobs(): boolean {
    return this.numberOfDoneJobs > 0;
  }

  get hasScheduledJobs(): boolean {
    return this.numberOfScheduledJobs > 0;
  }

  get hasFailedJobs(): boolean {
    return this.numberOfFailedJobs > 0;
  }

  get numberOfDoneJobs(): number {
    if (this.isLoading || !this.currentOrder) {
      return 0;
    }

    return this.currentOrder!.dataDistribution
      .map((dataDistribution) => dataDistribution.jobs)
      .flat()
      .filter((job) => job.status === 'DONE')
      .length;
  }

  get numberOfScheduledJobs(): number {
    if (this.isLoading || !this.currentOrder) {
      return 0;
    }

    return this.currentOrder.dataDistribution
      .map((dataDistribution) => dataDistribution.jobs)
      .flat()
      .filter((job) => job.status === 'SCHEDULED')
      .length;
  }

  get numberOfFailedJobs(): number {
    if (this.isLoading || !this.currentOrder) {
      return 0;
    }

    return this.currentOrder!.dataDistribution
      .map((dataDistribution) => dataDistribution.jobs)
      .flat()
      .filter((job) => job.status !== 'DONE' && job.status !== 'SCHEDULED')
      .length;
  }

  mounted(): void {
    if (!this.currentOrderId) {
      this.setCurrentOrderId(this.$route.params.orderId);
    }

    this.updateCurrentOrder();
  }

  @Watch('$route.path')
  updateCurrentOrder(): void {
    this.currentTab = defaultTab;
    this.getCurrentOrder()
      .catch((error) => {
        const message = error.response?.data.message || 'Der aktuelle Auftrag konnte nicht geladen werden';
        showError({ message });
      });
  }

}
